import React, { useMemo, useState } from 'react';
import { SimulatorModel } from '../../../models/SimulatorModel';
import { SimulatorSettingsModel as SimulatorSettingsDto } from '../../../models/superbit-api-models';
import { COMPANY_TABS, COMPANY_CONFIGS } from '../companies.config';
import EditableTable, { CompanyOption } from '../components/EditableTable';
import TermsAndCoversModal from '../components/TermsAndCoversModal';
import TermsAndCoversAgentModal from '../components/TermsAndCoversAgentModal';
import './CompaniesSettings.css';

interface CompaniesSettingsProps {
    api: SimulatorModel;
    settings: SimulatorSettingsDto;
}

type Feedback = { ok: boolean; msg: string } | null;

// true when a rich-text value holds real content (ignores empty tags / whitespace).
const hasContent = (v?: string): boolean => {
    if (!v) return false;
    return v.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length > 0;
};

// row has terms when any of its text fields is filled (routes: 4, agents: 3).
const hasRouteTerms = (r: any): boolean =>
    hasContent(r?.temsAndCovers) ||
    hasContent(r?.premiumExpansion) ||
    hasContent(r?.text) ||
    hasContent(r?.text2);

const hasAgentTerms = (r: any): boolean =>
    hasContent(r?.temsAndCovers) ||
    hasContent(r?.premiumExpansion) ||
    hasContent(r?.text);

const groupByCompany = (items: any[] | undefined): Record<string, any[]> => {
    const map: Record<string, any[]> = {};
    (items ?? []).forEach((item) => {
        const id = (item.insuranceCompanyId ?? '').toString().toLowerCase();
        if (!map[id]) map[id] = [];
        map[id].push(item);
    });
    return map;
};

/** "חברות" tab — per-company Routes + Agents tables (was _CompaniesSettings.cshtml). */
const CompaniesSettings: React.FC<CompaniesSettingsProps> = ({ api, settings }) => {
    const [activeCompany, setActiveCompany] = useState<string>(COMPANY_TABS[0].key);

    const [routesMap, setRoutesMap] = useState<Record<string, any[]>>(() =>
        groupByCompany(settings.routes)
    );
    const [agentsMap, setAgentsMap] = useState<Record<string, any[]>>(() =>
        groupByCompany(settings.agentDetailsList)
    );

    const [savingRoutes, setSavingRoutes] = useState(false);
    const [savingAgents, setSavingAgents] = useState(false);
    const [routesFeedback, setRoutesFeedback] = useState<Feedback>(null);
    const [agentsFeedback, setAgentsFeedback] = useState<Feedback>(null);

    // route/agent id whose "תנאים וכיסויים" modal is open (null = closed)
    const [termsRouteId, setTermsRouteId] = useState<string | null>(null);
    const [termsAgentId, setTermsAgentId] = useState<string | null>(null);

    const companyOptions: CompanyOption[] = useMemo(
        () =>
            (settings.insuranceCompanies ?? []).map((c) => ({
                value: c.value ?? '',
                label: c.text ?? '',
            })),
        [settings.insuranceCompanies]
    );

    const tab = COMPANY_TABS.find((t) => t.key === activeCompany)!;
    const companyId = tab.id.toLowerCase();
    const config = COMPANY_CONFIGS[companyId];

    const routeRows = routesMap[companyId] ?? [];
    const agentRows = agentsMap[companyId] ?? [];

    // options for the Hachshara route "agent" dropdown — the company's own agents
    const agentDynamicOptions = useMemo(
        () => ({
            hachsharaAgents: agentRows
                .filter((a) => a.id)
                .map((a) => ({
                    value: (a.id ?? '').toString().toLowerCase(),
                    label: a.agentNumber ?? '',
                })),
        }),
        [agentRows]
    );

    // --- routes handlers ---
    const setRouteRows = (rows: any[]) =>
        setRoutesMap((m) => ({ ...m, [companyId]: rows }));

    // terms & covers can only be edited on a persisted route (loaded/saved by id)
    const openTerms = (i: number) => {
        const row = routeRows[i];
        setRoutesFeedback(null);
        if (!row?.id) {
            setRoutesFeedback({ ok: false, msg: 'יש לשמור את המסלול לפני עריכת התנאים והכיסויים' });
            return;
        }
        setTermsRouteId(row.id);
    };

    const addRoute = () => setRouteRows(routeRows.concat(config.newRoute(companyId)));
    const deleteRoute = async (i: number) => {
        const rows = routeRows;
        const row = rows[i];
        setRoutesFeedback(null);
        try {
            if (row?.id) await api.simulatorDeleteRoute(row.id); // persist existing rows immediately
            setRouteRows(rows.filter((_, idx) => idx !== i));
            if (row?.id) setRoutesFeedback({ ok: true, msg: 'המסלול נמחק' });
        } catch {
            setRoutesFeedback({ ok: false, msg: 'מחיקת המסלול נכשלה' });
        }
    };

    const saveRoutes = async () => {
        setRoutesFeedback(null);
        const payload = routeRows.filter((r) => Number(r.routeId) > 0);
        if (payload.length === 0) {
            setRoutesFeedback({ ok: false, msg: 'אין מסלולים תקינים לשמירה' });
            return;
        }
        setSavingRoutes(true);
        try {
            await api.simulatorUpdateRoutes(payload);
            setRoutesFeedback({ ok: true, msg: 'המסלולים נשמרו בהצלחה' });
        } catch {
            setRoutesFeedback({ ok: false, msg: 'שמירת המסלולים נכשלה' });
        } finally {
            setSavingRoutes(false);
        }
    };

    // --- agents handlers ---
    const setAgentRows = (rows: any[]) =>
        setAgentsMap((m) => ({ ...m, [companyId]: rows }));

    // terms & covers can only be edited on a persisted agent (loaded/saved by id)
    const openAgentTerms = (i: number) => {
        const row = agentRows[i];
        setAgentsFeedback(null);
        if (!row?.id) {
            setAgentsFeedback({ ok: false, msg: 'יש לשמור את הסוכן לפני עריכת התנאים והכיסויים' });
            return;
        }
        setTermsAgentId(row.id);
    };

    const addAgent = () => setAgentRows(agentRows.concat(config.newAgent(companyId)));
    const deleteAgent = async (i: number) => {
        const rows = agentRows;
        const row = rows[i];
        setAgentsFeedback(null);
        try {
            if (row?.id) await api.simulatorDeleteAgent(row.id); // persist existing rows immediately
            setAgentRows(rows.filter((_, idx) => idx !== i));
            if (row?.id) setAgentsFeedback({ ok: true, msg: 'הסוכן נמחק' });
        } catch {
            setAgentsFeedback({ ok: false, msg: 'מחיקת הסוכן נכשלה' });
        }
    };

    const saveAgents = async () => {
        setAgentsFeedback(null);
        if (agentRows.length === 0) {
            setAgentsFeedback({ ok: false, msg: 'אין סוכנים לשמירה' });
            return;
        }
        setSavingAgents(true);
        try {
            await api.userUpdateAgents(agentRows);
            setAgentsFeedback({ ok: true, msg: 'פרטי הסוכנים נשמרו בהצלחה' });
        } catch {
            setAgentsFeedback({ ok: false, msg: 'שמירת פרטי הסוכנים נכשלה' });
        } finally {
            setSavingAgents(false);
        }
    };

    return (
        <div className="sbsim-companies">
            <nav className="sbsim-companies__tabs">
                {COMPANY_TABS.map((c) => {
                    const isActive = c.key === activeCompany;
                    const classes = [
                        'sbsim-companies__tab',
                        isActive ? 'sbsim-companies__tab--active' : '',
                        c.ready ? '' : 'sbsim-companies__tab--disabled',
                    ]
                        .filter(Boolean)
                        .join(' ');
                    return (
                        <button
                            key={c.key}
                            type="button"
                            className={classes}
                            onClick={() => c.ready && setActiveCompany(c.key)}
                            title={c.ready ? undefined : 'בקרוב'}
                        >
                            {c.label}
                        </button>
                    );
                })}
            </nav>

            {config ? (
                <>
                    <EditableTable
                        title="מסלולים"
                        columns={config.routeColumns}
                        rows={routeRows}
                        companyOptions={companyOptions}
                        dynamicOptions={agentDynamicOptions}
                        saving={savingRoutes}
                        feedback={routesFeedback}
                        rowActionLabel="תנאים וכיסויים"
                        rowActionTitle="תנאים וכיסויים"
                        onRowAction={openTerms}
                        rowActionActive={hasRouteTerms}
                        onChange={setRouteRows}
                        onAdd={addRoute}
                        onDelete={deleteRoute}
                        onSave={saveRoutes}
                    />
                    <EditableTable
                        title="פרטי סוכן"
                        columns={config.agentColumns}
                        rows={agentRows}
                        companyOptions={companyOptions}
                        saving={savingAgents}
                        feedback={agentsFeedback}
                        rowActionLabel="תנאים וכיסויים"
                        rowActionTitle="תנאים וכיסויים"
                        onRowAction={openAgentTerms}
                        rowActionActive={hasAgentTerms}
                        onChange={setAgentRows}
                        onAdd={addAgent}
                        onDelete={deleteAgent}
                        onSave={saveAgents}
                    />
                </>
            ) : (
                <div className="sbsim__placeholder">
                    החברה "{tab.label}" תיבנה בשלב הבא.
                </div>
            )}

            {termsRouteId && (
                <TermsAndCoversModal
                    api={api}
                    routeId={termsRouteId}
                    onClose={() => setTermsRouteId(null)}
                    onSaved={(saved) => {
                        // write back onto the row so the table marker updates live
                        setRoutesMap((m) => ({
                            ...m,
                            [companyId]: (m[companyId] ?? []).map((r) =>
                                r.id === termsRouteId ? Object.assign(r, saved) : r
                            ),
                        }));
                        setRoutesFeedback({ ok: true, msg: 'התנאים והכיסויים נשמרו' });
                    }}
                />
            )}

            {termsAgentId && (
                <TermsAndCoversAgentModal
                    api={api}
                    agentDetailsId={termsAgentId}
                    onClose={() => setTermsAgentId(null)}
                    onSaved={(saved) => {
                        setAgentsMap((m) => ({
                            ...m,
                            [companyId]: (m[companyId] ?? []).map((r) =>
                                r.id === termsAgentId ? Object.assign(r, saved) : r
                            ),
                        }));
                        setAgentsFeedback({ ok: true, msg: 'התנאים והכיסויים נשמרו' });
                    }}
                />
            )}
        </div>
    );
};

export default CompaniesSettings;
